import {
  Box,
  Button,
  Drawer,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { useFormik } from "formik";
import { usePopup } from "hooks/usePopup";
import { useEffect } from "react";
import { HiClock, HiExclamationCircle } from "react-icons/hi";
import batchServiceV3 from "services/batchServiceV3";
import * as Yup from "yup";

import { formatDueStatus } from "../../utils/dueStatus";

export const EditQuantitySheet = ({ open, onClose, data, reload }) => {
  const { addPopup } = usePopup();
  const batch = data || {};
  const {
    id,
    classification,
    quantity,
    available_quantity,
    manual_counted_quantity,
    unit
  } = batch;
  const productName = batch.product?.name;
  const unitLabel = unit || "";

  const status = formatDueStatus(batch.minutes_to_due);
  const accent = status.overdue ? "#EB001B" : "#EC9C24";
  const StatusIcon = status.overdue ? HiExclamationCircle : HiClock;

  const infoParts = [
    id != null ? `Lote ${id}` : null,
    classification ? `Classe ${classification}` : null,
    unitLabel ? `Unidade ${unitLabel}` : null
  ].filter(Boolean);

  const receivedQty =
    typeof quantity === "number" && Number.isFinite(quantity) ? quantity : null;

  let quantitySchema = Yup.number()
    .typeError("Informe um número válido")
    .required("Campo obrigatório")
    .min(0, "A quantidade não pode ser negativa");
  if (receivedQty != null) {
    quantitySchema = quantitySchema.max(
      receivedQty,
      `Não pode ser maior que o recebido (${receivedQty}${
        unitLabel ? ` ${unitLabel}` : ""
      })`
    );
  }
  const validationSchema = Yup.object().shape({
    manualCountedQuantity: quantitySchema
  });

  const helperHint =
    receivedQty != null
      ? `Informe a quantidade atual (máx: ${receivedQty}${
          unitLabel ? ` ${unitLabel}` : ""
        })`
      : "Informe a quantidade atual do lote";

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      manualCountedQuantity: ""
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: async values => {
      try {
        const payload = {
          ...values,
          manualCountedQuantity: Number(values.manualCountedQuantity)
        };
        await batchServiceV3.updateBatchManualCount(id, payload);
        addPopup({
          type: "success",
          title: `Lote ${id} atualizado com sucesso!`,
          description: `Quantidade registrada para o Lote ${id}.`
        });
        reload && reload();
        onClose();
      } catch (error) {
        const response = error?.response;
        addPopup({
          type: "error",
          title: `Erro ao atualizar o lote "${id}"`,
          description: `Erro: ${response?.data?.msg || error}.`
        });
      } finally {
        formik.setSubmitting(false);
      }
    }
  });

  // Reseta o formulário toda vez que o sheet abre, pra não herdar o valor
  // digitado na abertura anterior (inclusive quando o valor inicial coincide).
  useEffect(() => {
    if (open) {
      formik.resetForm({
        values: {
          manualCountedQuantity: ""
        }
      });
    }
  }, [open, id]);

  const showError =
    (formik.touched.manualCountedQuantity || formik.submitCount > 0) &&
    !!formik.errors.manualCountedQuantity;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          padding: "0.75rem 1.25rem 1.5rem",
          width: "100%",
          maxWidth: 520,
          margin: "0 auto",
          boxSizing: "border-box"
        }
      }}
    >
      <Box
        style={{
          width: 40,
          height: 4,
          borderRadius: 4,
          background: "#D1D5DB",
          margin: "0 auto 14px"
        }}
      />

      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "1.25rem"
        }}
      >
        <Typography
          style={{
            fontSize: "1.375rem",
            fontWeight: 700,
            color: "#111827",
            fontFamily: "Inter"
          }}
        >
          Editar quantidade
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Fechar">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        style={{
          background: "#F5F6F7",
          borderRadius: 12,
          padding: "1rem 1.125rem",
          marginBottom: "1.5rem"
        }}
      >
        <Typography
          style={{
            fontSize: "1.125rem",
            fontWeight: 700,
            color: "#111827",
            fontFamily: "Inter"
          }}
        >
          {productName}
        </Typography>
        <Typography
          style={{
            fontSize: "0.9375rem",
            color: "#6B7280",
            margin: "4px 0 8px",
            fontFamily: "Inter"
          }}
        >
          {infoParts.join("  •  ")}
        </Typography>
        <Box style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <StatusIcon size={18} color={accent} />
          <Typography
            style={{
              fontSize: "0.9375rem",
              fontWeight: 500,
              color: accent,
              fontFamily: "Inter"
            }}
          >
            {status.text}
          </Typography>
        </Box>
      </Box>

      <form onSubmit={formik.handleSubmit} noValidate>
        <Typography
          style={{
            fontSize: "0.9375rem",
            fontWeight: 500,
            color: "#374151",
            marginBottom: 8,
            fontFamily: "Inter"
          }}
        >
          Quantidade disponível
        </Typography>
        <TextField
          fullWidth
          autoFocus
          type="number"
          name="manualCountedQuantity"
          variant="outlined"
          value={formik.values.manualCountedQuantity}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={showError}
          helperText={
            (showError && formik.errors.manualCountedQuantity) || helperHint
          }
          inputProps={{
            step: "any",
            min: 0,
            ...(receivedQty != null ? { max: receivedQty } : {})
          }}
          InputProps={
            unitLabel
              ? {
                  endAdornment: (
                    <InputAdornment position="end">{unitLabel}</InputAdornment>
                  )
                }
              : undefined
          }
        />

        <Box style={{ display: "flex", gap: 12, marginTop: "1.5rem" }}>
          <Button
            fullWidth
            type="button"
            variant="outlined"
            color="primary"
            size="large"
            onClick={onClose}
            style={{ minHeight: 48, borderRadius: 10, fontWeight: 700 }}
          >
            Cancelar
          </Button>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={formik.isSubmitting}
            style={{
              minHeight: 48,
              borderRadius: 10,
              fontWeight: 700,
              color: "#FFF"
            }}
          >
            Salvar
          </Button>
        </Box>
      </form>
    </Drawer>
  );
};
